// client/src/pages/AdminProPlanSource.js - Pro Plan Purchase Source Attribution
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  PieChart, Pie, Cell, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const SOURCE_COLORS = {
  'Paid': '#EF4444', 'Organic Search': '#10B981', 'AI Search': '#8B5CF6',
  'Social': '#F59E0B', 'Email': '#3B82F6', 'Referral': '#06B6D4',
  'Direct': '#6B7280', 'Unknown': '#D1D5DB'
};

const s = {
  container: { maxWidth: 1400, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 24, fontWeight: 700, fontFamily: 'var(--font-display)', color: 'var(--ink)' },
  subtitle: { color: 'var(--muted)', fontSize: 13, marginTop: 4 },
  dateRow: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  dateInput: { padding: '8px 12px', border: '1px solid var(--line)', borderRadius: 0, fontSize: 13, fontFamily: 'var(--font-body)', outline: 'none' },
  btn: { background: 'transparent', border: '1.5px solid var(--ink)', color: 'var(--ink)', padding: '7px 14px', borderRadius: 0, fontSize: 12, cursor: 'pointer' },
  refreshBtn: { background: 'none', border: '1.5px solid var(--ink)', color: 'var(--ink)', borderRadius: 0, padding: '7px 10px', cursor: 'pointer', fontSize: 14 },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 24 },
  statCard: { background: 'var(--card)', border: '1px solid var(--line-card)', borderRadius: 0, padding: '18px 16px 14px', transition: 'all 0.2s' },
  statCardHighlight: { background: 'var(--ink)', color: 'var(--paper)', border: '1px solid var(--ink)' },
  statLabel: { fontSize: 11, letterSpacing: '1.5px', textTransform: 'uppercase', fontFamily: 'var(--font-mono)', marginBottom: 6, color: 'var(--muted-2)' },
  statValue: { fontSize: 26, fontWeight: 700, fontFamily: 'var(--font-mono)', lineHeight: 1, color: 'var(--ink)' },
  statSub: { fontSize: 11, marginTop: 6 },
  card: { background: 'var(--card)', border: '1px solid var(--line-card)', borderRadius: 0, padding: 24, marginBottom: 20 },
  cardTitle: { fontSize: 11, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'var(--muted-2)', marginBottom: 16, fontFamily: 'var(--font-mono)' },
  row: { display: 'grid', gap: 20, marginBottom: 20 },
  row2: { gridTemplateColumns: '2fr 3fr' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'var(--muted-2)', borderBottom: '2px solid var(--line-card)', fontFamily: 'var(--font-mono)', background: 'var(--paper)' },
  thRight: { textAlign: 'right' },
  td: { padding: '10px 12px', fontSize: 13, borderBottom: '1px solid var(--line-card)', color: 'var(--ink)' },
  tdRight: { textAlign: 'right', fontFamily: 'var(--font-mono)' },
  badge: { display: 'inline-flex', padding: '2px 8px', borderRadius: 0, fontSize: 11, fontWeight: 600, fontFamily: 'var(--font-mono)' },
  select: { padding: '8px 12px', border: '1px solid var(--line)', borderRadius: 0, fontSize: 13, outline: 'none', fontFamily: 'var(--font-body)' },
  alert: { background: 'var(--paper-2)', border: '1px solid var(--line)', color: 'var(--ink)', padding: '10px 14px', borderRadius: 0, marginBottom: 16, fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }
};

const getSourceBadge = (type, platform) => {
  const color = SOURCE_COLORS[type] || '#6B7280';
  return (
    <span style={{ ...s.badge, background: `${color}15`, color, border: `1px solid ${color}30` }}>
      {type} — {platform}
    </span>
  );
};

export default function AdminProPlanSource() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 3);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  const fetchData = async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true); else setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const { data: result } = await axios.get(`/api/admin/analytics/proplan-source?${params}`);
      if (result.success) setData(result.data);
      else throw new Error(result.error);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { fetchData(); }, [startDate, endDate]); // eslint-disable-line

  const runBackfill = async () => {
    try {
      setBackfilling(true); setBackfillResult(null);
      const { data: result } = await axios.post('/api/admin/analytics/backfill-attribution');
      if (result.success) {
        setBackfillResult(result.data.message);
        fetchData(true);
      } else {
        setBackfillResult('Error: ' + (result.error || 'Unknown'));
      }
    } catch (err) {
      setBackfillResult('Error: ' + err.message);
    } finally { setBackfilling(false); }
  };

  const getTypeCount = (type) => {
    const found = (data?.summary?.byType || []).find(t => t.type === type);
    return found ? found.count : 0;
  };
  const getTypeRevenue = (type) => {
    const found = (data?.summary?.byType || []).find(t => t.type === type);
    return found ? found.revenue : 0;
  };

  // Pie chart data
  const pieData = (data?.summary?.byType || []).map(t => ({
    name: t.type, value: t.count, revenue: t.revenue, color: SOURCE_COLORS[t.type] || '#6B7280'
  }));

  // Platform bar chart
  const platformData = (data?.byPlatform || []).slice(0, 10).map(p => ({
    name: p.platform, type: p.type, bookings: p.count, revenue: p.revenue
  }));

  // Filtered recent signups
  const filteredSignups = (data?.recentBookings || []).filter(b =>
    filterType === 'all' || b.source_type === filterType
  );

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', color: 'var(--muted)' }}>Loading pro plan source data...</div>;

  return (
    <div style={s.container}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Pro Plan Source</h1>
          <p style={s.subtitle}>Track where your Pro Plan subscriptions come from — Paid, Organic, AI Search, Social, and more</p>
        </div>
        <div style={s.dateRow}>
          <label style={{ fontSize: 11, color: 'var(--muted)' }}>From</label>
          <input type="date" style={s.dateInput} value={startDate} onChange={e => setStartDate(e.target.value)} />
          <label style={{ fontSize: 11, color: 'var(--muted)' }}>To</label>
          <input type="date" style={s.dateInput} value={endDate} onChange={e => setEndDate(e.target.value)} />
          <button style={s.btn} onClick={runBackfill} disabled={backfilling}>
            {backfilling ? 'Reclassifying...' : 'Fix Unknown Sources'}
          </button>
          <button style={s.refreshBtn} onClick={() => fetchData(true)} disabled={refreshing}>
            {refreshing ? '...' : '↻'}
          </button>
        </div>
      </div>

      {backfillResult && (
        <div style={s.alert}>
          {backfillResult}
          <button onClick={() => setBackfillResult(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}>×</button>
        </div>
      )}
      {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: 'var(--danger)', padding: '10px 14px', borderRadius: 0, marginBottom: 16, fontSize: 13 }}>{error}</div>}

      {/* Summary Cards */}
      <div style={s.statsGrid}>
        <div style={{ ...s.statCard, ...s.statCardHighlight }}>
          <div style={{ ...s.statLabel, color: 'rgba(244,241,234,0.6)' }}>Total Pro Signups</div>
          <div style={{ ...s.statValue, color: 'var(--paper)' }}>{data?.summary?.totalBookings || 0}</div>
          <div style={{ ...s.statSub, color: 'rgba(244,241,234,0.5)' }}>${(data?.summary?.totalRevenue || 0).toFixed(2)} revenue</div>
        </div>
        {[
          { type: 'Paid', icon: '$', color: '#EF4444' },
          { type: 'Organic Search', icon: '◉', color: '#10B981' },
          { type: 'AI Search', icon: '◈', color: '#8B5CF6' },
          { type: 'Social', icon: '⟡', color: '#F59E0B' },
          { type: 'Referral', icon: '⟜', color: '#06B6D4' },
          { type: 'Email', icon: '✉', color: '#3B82F6' },
          { type: 'Direct', icon: '→', color: '#6B7280' }
        ].map(({ type, color }) => (
          <div key={type} style={s.statCard}>
            <div style={{ ...s.statLabel, color }}>{type}</div>
            <div style={s.statValue}>{getTypeCount(type)}</div>
            <div style={{ ...s.statSub, color: 'var(--muted)' }}>${getTypeRevenue(type).toFixed(2)} revenue</div>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div style={{ ...s.row, ...s.row2 }}>
        {/* Source Type Pie */}
        <div style={s.card}>
          <div style={s.cardTitle}>Source Type Breakdown</div>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={110} paddingAngle={3} dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip formatter={(value, name, props) => [`${value} signups ($${props.payload.revenue?.toFixed(2) || 0})`, name]} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '100px 0' }}>No data yet</div>}
        </div>

        {/* Platform Bar Chart */}
        <div style={s.card}>
          <div style={s.cardTitle}>Signups by Platform</div>
          {platformData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={platformData} layout="vertical" margin={{ left: 100 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={95} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value, name, props) => [`${value} signups ($${props.payload.revenue?.toFixed(2)})`, props.payload.type]} />
                <Bar dataKey="bookings" radius={[0, 0, 0, 0]}>
                  {platformData.map((entry, i) => <Cell key={i} fill={SOURCE_COLORS[entry.type] || '#6B7280'} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '100px 0' }}>No data yet</div>}
        </div>
      </div>

      {/* Campaign Performance */}
      {(data?.byCampaign || []).length > 0 && (
        <div style={s.card}>
          <div style={s.cardTitle}>Campaign Performance</div>
          <table style={s.table}>
            <thead><tr>
              <th style={s.th}>Source</th>
              <th style={s.th}>Campaign</th>
              <th style={{ ...s.th, ...s.thRight }}>Signups</th>
              <th style={{ ...s.th, ...s.thRight }}>Revenue</th>
            </tr></thead>
            <tbody>
              {data.byCampaign.map((c, i) => (
                <tr key={i}>
                  <td style={s.td}>{c.source}</td>
                  <td style={{ ...s.td, fontFamily: 'var(--font-mono)', fontSize: 12 }}>{c.campaign}</td>
                  <td style={{ ...s.td, ...s.tdRight }}>{c.count}</td>
                  <td style={{ ...s.td, ...s.tdRight }}>${c.revenue.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Top Landing Pages */}
      {(data?.byLandingPage || []).length > 0 && (
        <div style={s.card}>
          <div style={s.cardTitle}>Top Landing Pages</div>
          <table style={s.table}>
            <thead><tr>
              <th style={s.th}>Landing Page</th>
              <th style={{ ...s.th, ...s.thRight }}>Signups</th>
              <th style={{ ...s.th, ...s.thRight }}>Revenue</th>
            </tr></thead>
            <tbody>
              {data.byLandingPage.map((l, i) => (
                <tr key={i}>
                  <td style={{ ...s.td, fontFamily: 'var(--font-mono)', fontSize: 12 }}>{l.landingPage}</td>
                  <td style={{ ...s.td, ...s.tdRight }}>{l.count}</td>
                  <td style={{ ...s.td, ...s.tdRight }}>${l.revenue.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Recent Pro Signups Table */}
      <div style={s.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
          <div style={s.cardTitle}>Recent Pro Signups by Source</div>
          <select style={s.select} value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="all">All Sources</option>
            <option value="Paid">Paid</option>
            <option value="Organic Search">Organic Search</option>
            <option value="AI Search">AI Search</option>
            <option value="Social">Social</option>
            <option value="Email">Email</option>
            <option value="Referral">Referral</option>
            <option value="Direct">Direct</option>
          </select>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={s.table}>
            <thead><tr>
              <th style={s.th}>Date</th>
              <th style={s.th}>User</th>
              <th style={s.th}>Plan</th>
              <th style={s.th}>Amount</th>
              <th style={s.th}>Source</th>
              <th style={s.th}>Campaign</th>
              <th style={s.th}>Landing Page</th>
            </tr></thead>
            <tbody>
              {filteredSignups.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ ...s.td, textAlign: 'center', padding: 32, color: 'var(--muted)' }}>
                    {(data?.recentBookings || []).length === 0
                      ? 'No Pro signups with attribution data yet. New Pro subscriptions will appear here with source tracking.'
                      : 'No signups match the selected filter.'}
                  </td>
                </tr>
              ) : filteredSignups.map((b, i) => (
                <tr key={i}>
                  <td style={{ ...s.td, fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                    {new Date(b.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </td>
                  <td style={{ ...s.td, fontWeight: 500 }}>{b.patient}</td>
                  <td style={s.td}>
                    <span style={{ ...s.badge, background: b.plan === 'annual' ? 'rgba(10,10,10,0.09)' : '#1d4ed818', color: b.plan === 'annual' ? 'var(--ink)' : '#1d4ed8' }}>
                      {b.service}
                    </span>
                  </td>
                  <td style={{ ...s.td, fontFamily: 'var(--font-mono)' }}>${(b.amount || 0).toFixed(2)}</td>
                  <td style={s.td}>{getSourceBadge(b.source_type, b.source_platform)}</td>
                  <td style={{ ...s.td, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' }}>{b.utm_campaign || '-'}</td>
                  <td style={{ ...s.td, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' }}>{b.landing_page || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
